import { capitalize, get } from "lodash";
import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import {
  getRevenueOpportunitiesTableColumns,
  getRevenueOpportunitiesTableFilters,
} from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableColumnsAndFilters";
import revOppsConstants from "constants/RevenueOpportunity";
import tagConstants from "constants/Tag";
import { formatUserName } from "helpers/user";
import CustomFieldService from "services/CustomField";
import RevenueOpportunityService from "services/RevenueOpportunities";
import TagService from "services/Tag";
import UserService from "services/User";
import {
  addCustomFieldsColumns,
  addResizedColumns,
} from "hooks/useTableConfig";
import { useRegionCountryForFilters } from "hooks/useRegionCountryForFilters";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import { revenueOpportunityKeys } from "components/RevenueOpportunitiesTableV3/queryKeys";

const fetchPurchaseOrdersOptions = async () => {
  const values = await RevenueOpportunityService.getPurchaseOrders();
  const optionsArr =
    values?.map(value => ({
      id: value,
      name: value,
      value: value,
    })) || [];
  return optionsArr;
};

const fetchSalesOrdersOptions = async () => {
  const values = await RevenueOpportunityService.getSalesOrders();
  const optionsArr =
    values?.map(value => ({
      id: value,
      name: value,
      value: value,
    })) || [];
  return optionsArr;
};

const fetchInvoicesOptions = async () => {
  const values = await RevenueOpportunityService.getInvoices();
  const optionsArr =
    values?.map(value => ({
      id: value,
      name: value,
      value: value,
    })) || [];
  return optionsArr;
};

const fetchAssigneeOptions = async () => {
  const revenueOpportunityAssignees =
    await RevenueOpportunityService.getRevenueOpportunityAssignees();
  const revenueOpportunityAssigneesArr = revenueOpportunityAssignees
    .filter(user => user.id > 0)
    .map(({ id, full_name, deleted, status }) => ({
      id: id,
      name: deleted
        ? `${full_name} (DELETED)`
        : formatUserName(full_name, status),
      value: id,
    }));
  return revenueOpportunityAssigneesArr;
};

const fetchCustomFields = async () => {
  const customFields = await RevenueOpportunityService.getCustomFields();
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel(
      "RevenueOpportunity"
    );

  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Revenue Opportunities",
      fields: customFields,
      model: "RevenueOpportunity",
      configs,
    },
  ];
};

const fetchRevenueOpportunityStatus = async () => {
  const options =
    await RevenueOpportunityService.getRevenueOpportunityStatusesForFilters();
  const optionsArr = options.map(option => ({
    id: option.id,
    text: option.name,
    value: option.id,
    name: option.name,
  }));
  return optionsArr;
};

const fetchTags = async className => {
  let taggableModels = tagConstants.taggableModels;
  let model = null;
  if (className === "EmailScheduleContacts") {
    model = "contact";
  } else {
    model = Object.keys(taggableModels).find(
      key => taggableModels[key] === className
    );
  }
  const tags = await TagService.getTagOptions({ model });
  return tags;
};

const recipientTypeOptions = Object.keys(revOppsConstants.recipientType).map(
  option => ({
    id: option,
    name: capitalize(revOppsConstants.recipientType[option]),
    value: option,
  })
);

export const useRevenueOpportunitiesTable = ({
  entityId,
  caseId,
  campaignId,
  contactId,
  tableName,
  className,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", "options", className],
    queryFn: () => fetchTags(className),
  });
  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", tableName],
    queryFn: fetchCustomFields,
  });
  const { data: statusOptions, isSuccess: isStatusOptionsFetched } = useQuery({
    queryKey: ["status-options", tableName],
    queryFn: fetchRevenueOpportunityStatus,
  });

  const { data: assigneeOptions, isSuccess: isAssigneeOptionsFetched } =
    useQuery({
      queryKey: ["assignee-options", tableName],
      queryFn: fetchAssigneeOptions,
    });
  const {
    data: purchaseOrdersOptions,
    isSuccess: isPurchaseOrdersOptionsFetched,
  } = useQuery({
    queryKey: ["purchase-orders-options", tableName],
    queryFn: fetchPurchaseOrdersOptions,
  });
  const { data: salesOrdersOptions, isSuccess: isSalesOrdersOptionsFetched } =
    useQuery({
      queryKey: ["sales-orders-options", tableName],
      queryFn: fetchSalesOrdersOptions,
    });
  const { data: invoicesOptions, isSuccess: isInvoicesOptionsFetched } =
    useQuery({
      queryKey: ["invoices-options", tableName],
      queryFn: fetchInvoicesOptions,
    });
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: () => UserService.getUserSettings(),
  });

  const { regionsAndCountries, isRegionsAndCountriesFetched } =
    useRegionCountryForFilters();

  const queryKey = useMemo(() => {
    if (entityId && campaignId)
      return revenueOpportunityKeys.list({ entityId, campaignId });
    if (entityId && caseId)
      return revenueOpportunityKeys.list({ entityId, caseId });
    if (campaignId) return revenueOpportunityKeys.list({ campaignId });
    if (contactId) return revenueOpportunityKeys.list({ contactId });
    if (entityId) return revenueOpportunityKeys.list({ entityId });
    return revenueOpportunityKeys.list();
  }, [entityId, campaignId, caseId, contactId]);

  const invalidateData = useCallback(async () => {
    // Awaited together: each call resolves only once its matching active
    // queries have refetched, so chaining them would serialise the round trips.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      // These filter options are distinct values read off the opportunity rows,
      // so a new order number or assignee has to reach the dropdowns.
      queryClient.invalidateQueries({ queryKey: ["purchase-orders-options"] }),
      queryClient.invalidateQueries({ queryKey: ["sales-orders-options"] }),
      queryClient.invalidateQueries({ queryKey: ["invoices-options"] }),
      queryClient.invalidateQueries({ queryKey: ["assignee-options"] }),
    ]);
  }, [queryKey]);

  const deleteRevenueOpportunity = useCallback(
    async revenueOpportunityId => {
      await RevenueOpportunityService.deleteRevenueOpportunity(
        revenueOpportunityId
      );
      await invalidateData();
    },
    [invalidateData]
  );

  const checkForLockedRows = useCallback(
    selectedData => selectedData.some(row => row.is_locked),
    []
  );

  const filters = useMemo(() => {
    if (
      !isTagsFetched ||
      !isCustomFieldsFetched ||
      !isStatusOptionsFetched ||
      !isAssigneeOptionsFetched ||
      !isPurchaseOrdersOptionsFetched ||
      !isSalesOrdersOptionsFetched ||
      !isInvoicesOptionsFetched ||
      !isTableSettingsFetched ||
      !isRegionsAndCountriesFetched
    ) {
      return [];
    }
    const finalFilters = getRevenueOpportunitiesTableFilters({
      tags,
      revenueOpportunityAssigneeOptions: assigneeOptions,
      purchaseOrdersOptions,
      salesOrdersOptions,
      invoicesOptions,
      recipientTypeOptions,
      statusOptions,
      entityId,
      contactId,
      countries: regionsAndCountries.countries,
      regions: regionsAndCountries.regions,
    });

    finalFilters.push(
      ...buildCustomFieldFilters(
        customFields,
        finalFilters.map(f => f.title)
      )
    );

    return finalFilters;
  }, [
    isTagsFetched,
    isCustomFieldsFetched,
    isStatusOptionsFetched,
    isAssigneeOptionsFetched,
    isPurchaseOrdersOptionsFetched,
    isSalesOrdersOptionsFetched,
    isInvoicesOptionsFetched,
    isTableSettingsFetched,
    isRegionsAndCountriesFetched,
    tags,
    purchaseOrdersOptions,
    salesOrdersOptions,
    invoicesOptions,
    assigneeOptions,
    customFields,
    statusOptions,
    entityId,
    contactId,
    regionsAndCountries,
  ]);

  const { enable_product_pricing } = useRuvixxFlags();
  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getRevenueOpportunitiesTableColumns({
      deleteRevenueOpportunity,
      invalidateData,
      isPricingEnabled: Boolean(enable_product_pricing),
    });
    const campaignsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: campaignsTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: campaignsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    customFields,
    tableSettings,
    isTableSettingsFetched,
    isCustomFieldsFetched,
    tableName,
    invalidateData,
    deleteRevenueOpportunity,
    enable_product_pricing,
  ]);

  return {
    columns,
    filters,
    tags,
    customFields,
    queryKey,
    invalidateData,
    checkForLockedRows,
  };
};
